import React, { Fragment } from "react";
import {
    BulkDeleteButton,
    Create,
    Datagrid,
    DateField,
    DeleteButton,
    List,
    ListButton,
    maxValue,
    minValue,
    number,
    NumberInput,
    BooleanInput,
    NumberField,
    RefreshButton,
    required,
    SimpleForm,
    TextField,
    TopToolbar,
    ReferenceField,
    ReferenceInput,
    SelectInput,
    Show,
    ShowButton,
    SimpleShowLayout,
    ReferenceManyField,
    downloadCSV,
    FilterList,
    FilterListItem,
    FilterLiveSearch,
    useGetList,
    usePermissions,
} from "react-admin";
import { Box, Card, CardContent } from "@mui/material";
import jsonExport from "jsonexport/dist";
import MarkAsSent from "../Button/MarkAsSent";

const createDefaultValues = {
    enabled: false,
};

const validateRequired = [required()];
const validateCodeCount = [number(), minValue(0), maxValue(10000)];

const PostEditActions = ({ basePath }) => (
    <TopToolbar>
        <ListButton basePath={basePath} />
        <RefreshButton />
    </TopToolbar>
);

const CodeBulkActionButtons = (props) => (
    <Fragment>
        <MarkAsSent label="Mark as Sent" {...props} />
        <BulkDeleteButton {...props} />
    </Fragment>
);

export const CustomDeleteButton = (props) => (
    <DeleteButton {...props} mutationMode="pessimistic" />
);

const ChannelIDFilter = () => {
    const { data, isLoading, error } = useGetList("Channel", {
        pagination: { page: 1, perPage: 10 },
        sort: { field: "id", order: "ASC" },
    });
    if (isLoading) {
        return <p>LOADING</p>;
    }
    if (error) {
        return <p>ERROR</p>;
    }
    console.log(data);
    return (
        <FilterList label="Channel ID">
            {data.map((record) => (
                <FilterListItem
                    label={record.name}
                    key={record.id}
                    value={{ channelId: record.id }}
                />
            ))}
        </FilterList>
    );
};

const CampaignIDFilter = () => {
    const { data, isLoading, error } = useGetList("Campaign", {
        pagination: { page: 1, perPage: 10 },
        sort: { field: "id", order: "ASC" },
    });
    if (isLoading) {
        return <p>LOADING</p>;
    }
    if (error) {
        return <p>ERROR</p>;
    }
    return (
        <FilterList label="Campaign ID">
            {data.map((record) => (
                <FilterListItem
                    label={record.name}
                    key={record.id}
                    value={{ campaignId: record.id }}
                />
            ))}
        </FilterList>
    );
};

const BundleIDFilter = () => {
    const { data, isLoading, error } = useGetList("Bundle", {
        pagination: { page: 1, perPage: 10 },
        sort: { field: "id", order: "ASC" },
    });
    if (isLoading) {
        return <p>LOADING</p>;
    }
    if (error) {
        return <p>ERROR</p>;
    }
    return (
        <FilterList label="Bundle ID">
            {data.map((record) => (
                <FilterListItem
                    label={record.name}
                    key={record.id}
                    value={{ bundleId: record.id }}
                />
            ))}
        </FilterList>
    );
};

const DateSentFilter = () => (
    <FilterList label="Date Sent">
        <FilterListItem
            label="Set"
            value={{
                dateSent: true,
            }}
        />
        <FilterListItem
            label="Not Set"
            value={{
                dateSent: false,
            }}
        />
    </FilterList>
);

const DateUsedFilter = () => (
    <FilterList label="Date First Used">
        <FilterListItem
            label="Set"
            value={{
                dateFirstUsed: true,
            }}
        />
        <FilterListItem
            label="Not Set"
            value={{
                dateFirstUsed: false,
            }}
        />
    </FilterList>
);

const FilterSidebar = () => (
    <Box
        sx={{
            display: {
                xs: "none",
                sm: "block",
            },
            order: -1, // display on the left rather than on the right of the list
            width: "15em",
            marginRight: "1em",
        }}
    >
        <Card>
            <CardContent>
                <FilterLiveSearch source="code" />
                <ChannelIDFilter />
                <CampaignIDFilter />
                <BundleIDFilter />
                <DateSentFilter source="dateSent" />
                <DateUsedFilter source="dateFirstUsed" />
            </CardContent>
        </Card>
    </Box>
);

const exporter = (records, fetchRelatedRecords) => {
    fetchRelatedRecords(records, "channelId", "Channel").then((channels) => {
        let finalData = records.map((record) => ({
            ...record,
            channelId: channels[record.channelId].name,
        }));
        fetchRelatedRecords(records, "bundleId", "Bundle").then((bundles) => {
            finalData = finalData.map((record) => ({
                ...record,
                bundleId: bundles[record.bundleId].name,
            }));
            fetchRelatedRecords(records, "campaignId", "Campaign").then(
                (campaigns) => {
                    finalData = finalData.map((record) => ({
                        ...record,
                        campaignId: campaigns[record.campaignId].name,
                    }));
                    jsonExport(
                        finalData,
                        {
                            rename: [
                                "ID",
                                "Code",
                                "ChannelID",
                                "CampaignID",
                                "BundleID",
                                "Max Usage",
                                "Date Created",
                                "Date Modified",
                            ],
                        },
                        (err, csv) => {
                            downloadCSV(csv, "CodeExport");
                        }
                    );
                }
            );
        });
    });
};

export const CodeList = (props) => {
    const { permissions } = usePermissions();
    return (
        <List
            {...props}
            aside={<FilterSidebar />}
            exporter={exporter}
            bulkActionButtons={<CodeBulkActionButtons />}
        >
            <Datagrid>
                <TextField label="Code" source="code" />
                {permissions === "ADMIN" && (
                    <ReferenceField
                        label="Channel ID"
                        reference="Channel"
                        source="channelId"
                    >
                        <TextField source="name" />
                    </ReferenceField>
                )}
                <ReferenceField
                    label="Campaign ID"
                    reference="Campaign"
                    source="campaignId"
                >
                    <TextField source="name" />
                </ReferenceField>
                {permissions === "ADMIN" && (
                    <ReferenceField
                        label="Bundle ID"
                        reference="Bundle"
                        source="bundleId"
                    >
                        <TextField source="name" />
                    </ReferenceField>
                )}
                <DateField label="Date Created" source="dateCreated" showTime />
                <DateField
                    label="Date Modified"
                    source="dateModified"
                    showTime
                />
                <DateField
                    label="Date Sent"
                    source="dateSent"
                    emptyText="Not Set"
                    showTime
                />
                <DateField
                    label="Date First Used"
                    source="dateFirstUsed"
                    emptyText="Not Set"
                    showTime
                />
                <ShowButton />
                <CustomDeleteButton />
            </Datagrid>
        </List>
    );
};

export const CodeShow = (props) => (
    <Show {...props}>
        <SimpleShowLayout>
            <TextField label="Code ID" source="id" />
            <TextField label="Code" source="code" />
            <ReferenceField
                label="Channel ID"
                reference="Channel"
                source="channelId"
            >
                <TextField source="name" />
            </ReferenceField>
            <ReferenceField
                label="Campaign ID"
                reference="Campaign"
                source="campaignId"
            >
                <TextField source="name" />
            </ReferenceField>
            <ReferenceField
                label="Bundle ID"
                reference="Bundle"
                source="bundleId"
            >
                <TextField source="name" />
            </ReferenceField>
            <NumberField label="Max Usage" source="maxUsage" />
            <TextField label="Description" source="description" />
            <TextField label="Url" source="url" />
            <DateField label="Date Created" source="dateCreated" showTime />
            <DateField label="Date Modified" source="dateModified" showTime />
            <DateField label="Date Sent" source="dateSent" showTime />
            <DateField
                label="Date First Used"
                source="dateFirstUsed"
                showTime
            />
            <DateField label="Date Last Used" source="dateLastUsed" showTime />
            <ReferenceManyField
                label="Consumed by"
                reference="CodeConsumption"
                target="code"
                source="code"
            >
                <Datagrid>
                    <TextField label="Platform" source="platform" />
                    <TextField label="Consumer Id" source="consumerId" />
                    <TextField label="IP Address" source="ipAddress" />
                    <TextField label="Country Code" source="countryCode" />
                    <DateField
                        label="Date Consumed"
                        source="dateConsumed"
                        showTime
                    />
                </Datagrid>
            </ReferenceManyField>
        </SimpleShowLayout>
    </Show>
);

export const CodeCreate = (props) => (
    <Create actions={<PostEditActions />} {...props}>
        <SimpleForm initialValues={createDefaultValues} redirect="list">
            <ReferenceInput
                label="Select Campaign ID"
                source="campaignId"
                reference="Campaign"
            >
                <SelectInput optionText="name" validate={validateRequired} />
            </ReferenceInput>
            <ReferenceInput
                label="Select Bundle ID"
                source="bundleId"
                reference="Bundle"
            >
                <SelectInput optionText="name" validate={validateRequired} />
            </ReferenceInput>
            <NumberInput
                label="Count"
                source="count"
                validate={validateCodeCount}
            />
            <BooleanInput
                label="Mark as Sent"
                source="markAsSent"
                validate={validateRequired}
            />
        </SimpleForm>
    </Create>
);
